/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import type { Config, Data, Datum, Layout, LayoutAxis, PlotData } from 'plotly.js';

export type ChartJsSupportedChartType = 'bar' | 'line' | 'scatter';
export type ChartJsColor = string | string[];
export type ChartJsDataPoint = Datum | { x: Datum; y: Datum };

export interface ChartJsDataset {
    backgroundColor?: ChartJsColor;
    borderColor?: ChartJsColor;
    borderWidth?: number;
    data: readonly ChartJsDataPoint[];
    fill?: boolean;
    label?: string;
    pointRadius?: number;
    stack?: string;
    type?: string;
    yAxisID?: string;
}

export interface ChartJsAxisOptions {
    beginAtZero?: boolean;
    display?: boolean;
    max?: number;
    min?: number;
    stacked?: boolean;
    title?: {
        display?: boolean;
        text?: string | string[];
    };
}

export interface ChartJsLegendOptions {
    display?: boolean;
    position?: 'top' | 'bottom' | 'left' | 'right';
}

export interface ChartJsTitleOptions {
    display?: boolean;
    text?: string | string[];
}

export interface ChartJsConfiguration {
    data: {
        datasets: readonly ChartJsDataset[];
        labels?: readonly Datum[];
    };
    options?: {
        plugins?: {
            legend?: ChartJsLegendOptions;
            title?: ChartJsTitleOptions;
        };
        scales?: Record<string, ChartJsAxisOptions>;
    };
    type: string;
}

export interface ChartJsToPlotlyConfiguration {
    config: Partial<Config>;
    data: Data[];
    layout: Partial<Layout>;
}

interface PlotlyBarTrace extends Partial<PlotData> {
    alignmentgroup?: string;
    offsetgroup?: string;
    type: 'bar';
}

type PlotlyLayoutWithAxes = Partial<Layout> & Record<string, Partial<LayoutAxis> | undefined>;

const SUPPORTED_CHART_TYPES = new Set<ChartJsSupportedChartType>(['bar', 'line', 'scatter']);

export class ChartJsToPlotlyConverter {
    public convert(chartJsConfig: ChartJsConfiguration): ChartJsToPlotlyConfiguration {
        this.assertSupportedChartType(chartJsConfig.type);

        const yAxisNames = this.createYAxisNames(chartJsConfig);
        const layout = this.convertLayout(chartJsConfig, yAxisNames);

        return {
            config: {
                responsive: true,
            },
            data: this.convertDatasets(chartJsConfig, yAxisNames),
            layout,
        };
    }

    private assertSupportedChartType(chartType: string): asserts chartType is ChartJsSupportedChartType {
        if (!SUPPORTED_CHART_TYPES.has(chartType as ChartJsSupportedChartType)) {
            throw new Error(`Unsupported Chart.js chart type: ${chartType}`);
        }
    }

    private convertDatasets(
        chartJsConfig: ChartJsConfiguration,
        yAxisNames: Map<string, string>,
    ): Data[] {
        const labels = chartJsConfig.data.labels ?? [];

        return chartJsConfig.data.datasets.map((dataset) => (
            this.convertDataset(dataset, chartJsConfig.type, labels, yAxisNames)
        ));
    }

    private convertDataset(
        dataset: ChartJsDataset,
        globalChartType: string,
        labels: readonly Datum[],
        yAxisNames: Map<string, string>,
    ): Data {
        const datasetType = dataset.type ?? globalChartType;

        this.assertSupportedChartType(datasetType);

        if (datasetType === 'bar') {
            return this.convertBarDataset(dataset, labels, yAxisNames);
        }

        return this.convertLineOrScatterDataset(dataset, datasetType, labels, yAxisNames);
    }

    private convertBarDataset(
        dataset: ChartJsDataset,
        labels: readonly Datum[],
        yAxisNames: Map<string, string>,
    ): Data {
        const marker: NonNullable<PlotData['marker']> = {};

        if (dataset.backgroundColor !== undefined) {
            marker.color = dataset.backgroundColor;
        }

        if (dataset.borderColor !== undefined || dataset.borderWidth !== undefined) {
            marker.line = {
                ...(dataset.borderColor !== undefined ? { color: dataset.borderColor } : {}),
                ...(dataset.borderWidth !== undefined ? { width: dataset.borderWidth } : {}),
            };
        }

        const trace: PlotlyBarTrace = {
            type: 'bar',
            x: [...labels],
            y: this.getYAxisValues(dataset.data),
            ...(dataset.label ? { name: dataset.label } : {}),
            ...(Object.keys(marker).length > 0 ? { marker } : {}),
            ...this.getTraceYAxis(dataset.yAxisID, yAxisNames),
        };

        if (dataset.stack) {
            trace.alignmentgroup = dataset.stack;
            trace.offsetgroup = dataset.stack;
        }

        return trace as Data;
    }

    private convertLineOrScatterDataset(
        dataset: ChartJsDataset,
        datasetType: Exclude<ChartJsSupportedChartType, 'bar'>,
        labels: readonly Datum[],
        yAxisNames: Map<string, string>,
    ): Data {
        const hasScatterPoints = datasetType === 'scatter'
            && dataset.data.some((point) => this.isScatterPoint(point));
        const xValues = hasScatterPoints
            ? dataset.data.map((point) => this.isScatterPoint(point) ? point.x : null)
            : [...labels];
        const yValues = hasScatterPoints
            ? dataset.data.map((point) => this.isScatterPoint(point) ? point.y : null)
            : this.getYAxisValues(dataset.data);
        const showMarkers = datasetType === 'scatter' || (dataset.pointRadius ?? 0) > 0;
        const trace: Partial<PlotData> = {
            type: 'scatter',
            mode: showMarkers ? 'lines+markers' : 'lines',
            x: xValues,
            y: yValues,
            ...(dataset.label ? { name: dataset.label } : {}),
            ...this.getTraceYAxis(dataset.yAxisID, yAxisNames),
        };

        if (dataset.borderColor !== undefined || dataset.borderWidth !== undefined) {
            trace.line = {
                ...(dataset.borderColor !== undefined ? { color: dataset.borderColor } : {}),
                ...(dataset.borderWidth !== undefined ? { width: dataset.borderWidth } : {}),
            };
        }

        if (showMarkers) {
            trace.marker = {
                ...(dataset.backgroundColor !== undefined ? { color: dataset.backgroundColor } : {}),
                ...(dataset.borderColor !== undefined ? { line: { color: dataset.borderColor } } : {}),
                ...(dataset.pointRadius !== undefined ? { size: dataset.pointRadius * 2 } : {}),
            };
        }

        if (dataset.fill) {
            trace.fill = 'tozeroy';

            if (dataset.backgroundColor !== undefined && typeof dataset.backgroundColor === 'string') {
                trace.fillcolor = dataset.backgroundColor;
            }
        }

        return trace as Data;
    }

    private convertLayout(
        chartJsConfig: ChartJsConfiguration,
        yAxisNames: Map<string, string>,
    ): Partial<Layout> {
        const layout: PlotlyLayoutWithAxes = {};

        this.convertAxes(layout, chartJsConfig.options?.scales, yAxisNames);
        this.convertLegend(layout, chartJsConfig.options?.plugins?.legend);
        this.convertTitle(layout, chartJsConfig.options?.plugins?.title);
        this.convertStacking(
            layout,
            chartJsConfig.data.datasets,
            chartJsConfig.type,
            chartJsConfig.options?.scales?.x?.stacked === true,
        );

        return layout;
    }

    private convertAxes(
        layout: PlotlyLayoutWithAxes,
        scales: Record<string, ChartJsAxisOptions> | undefined,
        yAxisNames: Map<string, string>,
    ): void {
        const xAxisOptions = scales?.x;

        if (xAxisOptions) {
            layout.xaxis = this.convertAxis(xAxisOptions);
        }

        yAxisNames.forEach((plotlyAxisName, chartJsAxisName) => {
            const layoutAxisName = plotlyAxisName === 'y' ? 'yaxis' : `yaxis${plotlyAxisName.slice(1)}`;
            const axis = this.convertAxis(scales?.[chartJsAxisName] ?? {});

            if (plotlyAxisName !== 'y') {
                axis.overlaying = 'y';
                axis.side = 'right';
            }

            layout[layoutAxisName] = axis;
        });
    }

    private convertAxis(axisOptions: ChartJsAxisOptions): Partial<LayoutAxis> {
        const titleText = this.getText(axisOptions.title?.text);

        return {
            ...(axisOptions.display !== undefined ? { visible: axisOptions.display } : {}),
            ...(axisOptions.min !== undefined || axisOptions.max !== undefined
                ? { range: [axisOptions.min ?? null, axisOptions.max ?? null] }
                : {}),
            ...(axisOptions.beginAtZero && axisOptions.min === undefined ? { rangemode: 'tozero' } : {}),
            ...(axisOptions.title?.display !== false && titleText ? { title: { text: titleText } } : {}),
        };
    }

    private convertLegend(
        layout: Partial<Layout>,
        legendOptions: ChartJsLegendOptions | undefined,
    ): void {
        if (!legendOptions) {
            return;
        }

        layout.showlegend = legendOptions.display ?? true;

        if (!legendOptions.position) {
            return;
        }

        const positions = {
            top: { orientation: 'h' as const, x: 0, y: 1.1 },
            bottom: { orientation: 'h' as const, x: 0, y: -0.2 },
            left: { x: -0.1, y: 1 },
            right: { x: 1.02, y: 1 },
        };

        layout.legend = positions[legendOptions.position];
    }

    private convertTitle(
        layout: Partial<Layout>,
        titleOptions: ChartJsTitleOptions | undefined,
    ): void {
        const titleText = this.getText(titleOptions?.text);

        if (titleOptions?.display !== false && titleText) {
            layout.title = { text: titleText };
        }
    }

    private convertStacking(
        layout: Partial<Layout>,
        datasets: readonly ChartJsDataset[],
        globalChartType: string,
        hasStackedXAxis: boolean,
    ): void {
        const hasDatasetStacks = datasets.some((dataset) => (
            (dataset.type ?? globalChartType) === 'bar' && Boolean(dataset.stack)
        ));
        const hasStackedBars = hasDatasetStacks || (globalChartType === 'bar' && hasStackedXAxis);

        if (hasStackedBars) {
            layout.barmode = 'stack';
        }
    }

    private createYAxisNames(chartJsConfig: ChartJsConfiguration): Map<string, string> {
        const chartJsAxisNames = new Set<string>(['y']);

        Object.keys(chartJsConfig.options?.scales ?? {})
            .filter((axisName) => axisName !== 'x')
            .forEach((axisName) => chartJsAxisNames.add(axisName));
        chartJsConfig.data.datasets.forEach((dataset) => {
            if (dataset.yAxisID) {
                chartJsAxisNames.add(dataset.yAxisID);
            }
        });

        const usedPlotlyAxisNames = new Set<string>();
        let nextAxisIndex = 2;

        return new Map(Array.from(chartJsAxisNames).map((chartJsAxisName) => {
            let plotlyAxisName = chartJsAxisName === 'y' ? 'y' : chartJsAxisName;

            if (plotlyAxisName === 'y1' || !/^y(?:[2-9]\d*)?$/.test(plotlyAxisName)
                || usedPlotlyAxisNames.has(plotlyAxisName)) {
                while (usedPlotlyAxisNames.has(`y${nextAxisIndex}`)) {
                    nextAxisIndex += 1;
                }

                plotlyAxisName = `y${nextAxisIndex}`;
                nextAxisIndex += 1;
            }

            usedPlotlyAxisNames.add(plotlyAxisName);

            return [chartJsAxisName, plotlyAxisName];
        }));
    }

    private getTraceYAxis(yAxisId: string | undefined, yAxisNames: Map<string, string>): { yaxis?: string } {
        const yaxis = yAxisNames.get(yAxisId ?? 'y') ?? 'y';

        return yaxis === 'y' ? {} : { yaxis };
    }

    private getYAxisValues(data: readonly ChartJsDataPoint[]): Datum[] {
        return data.map((point) => this.isScatterPoint(point) ? point.y : point);
    }

    private getText(text: string | string[] | undefined): string | undefined {
        if (Array.isArray(text)) {
            return text.join(' ');
        }

        return text;
    }

    private isScatterPoint(point: ChartJsDataPoint): point is { x: Datum; y: Datum } {
        return typeof point === 'object' && point !== null && 'x' in point && 'y' in point;
    }
}
